import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Mail, Phone, Building2, UserCheck, Download, Trash2, Bell, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GuestList = () => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    media: 0,
    volunteers: 0,
    regularGuests: 0,
    totalAttendees: 0
  });

  useEffect(() => {
    fetchGuests();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/rsvp-stats`);
      console.log('Stats:', response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchGuests = async () => {
    try {
      const response = await axios.get(`${API}/rsvp`);
      const data = response.data;
      setGuests(data);

      // Calculate stats - Count total PEOPLE (main person + additional guests), not just RSVPs
      const mediaPeople = data
        .filter(g => g.participantType === 'media')
        .reduce((sum, g) => sum + 1 + g.additionalGuests, 0);
      
      const volunteerPeople = data
        .filter(g => g.participantType === 'volunteer')
        .reduce((sum, g) => sum + 1 + g.additionalGuests, 0);
      
      const regularGuestPeople = data
        .filter(g => g.participantType === 'guest')
        .reduce((sum, g) => sum + 1 + g.additionalGuests, 0);
      
      const totalAttendees = data.reduce((sum, g) => sum + 1 + g.additionalGuests, 0);

      setStats({
        total: data.length,
        media: mediaPeople,
        volunteers: volunteerPeople,
        regularGuests: regularGuestPeople,
        totalAttendees
      });
    } catch (error) {
      console.error('Error fetching guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    // Create CSV content
    const headers = ['Nom', 'Email', 'Téléphone', 'Entreprise', 'Type', 'Nb Personnes', 'Média', 'Invités Supp.', 'Noms Invités', 'Statut', 'Date Inscription'];
    
    const rows = guests.map(guest => {
      const type = getParticipantTypeLabel(guest.participantType);
      const guestNames = guest.guestNames.join('; ');
      const date = new Date(guest.createdAt).toLocaleString('fr-CA');
      const status = guest.waitlist ? 'Liste d\'attente' : 'Confirmé';
      const totalPeople = 1 + guest.additionalGuests;
      
      return [
        guest.name,
        guest.email,
        guest.phone,
        guest.business || '',
        type,
        totalPeople,
        guest.mediaName || '',
        guest.additionalGuests,
        guestNames,
        status,
        date
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `liste-invites-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (guestId, guestName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${guestName} de la liste ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/rsvp/${guestId}`);
      toast.success(`${guestName} a été supprimé de la liste`);
      // Refresh the list
      fetchGuests();
    } catch (error) {
      console.error('Error deleting guest:', error);
      toast.error('Erreur lors de la suppression. Veuillez réessayer.');
    }
  };

  const sendReminder7Days = async () => {
    if (!window.confirm('Envoyer un rappel par email à tous les invités confirmés (7 jours avant) ?')) {
      return;
    }

    try {
      toast.loading('Envoi des emails en cours...');
      const response = await axios.post(`${API}/send-reminder-7days`);
      toast.dismiss();
      toast.success(`${response.data.sent} email(s) envoyé(s) avec succès!`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} email(s) n'ont pas pu être envoyés`);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error sending reminders:', error);
      toast.error('Erreur lors de l\'envoi des emails. Veuillez réessayer.');
    }
  };

  const sendReminder24Hours = async () => {
    if (!window.confirm('Envoyer un rappel par email à tous les invités confirmés (24h avant) ?')) {
      return;
    }

    try {
      toast.loading('Envoi des emails en cours...');
      const response = await axios.post(`${API}/send-reminder-24h`);
      toast.dismiss();
      toast.success(`${response.data.sent} email(s) envoyé(s) avec succès!`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} email(s) n'ont pas pu être envoyés`);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error sending reminders:', error);
      toast.error('Erreur lors de l\'envoi des emails. Veuillez réessayer.');
    }
  };

  const sendConfirmationsToAll = async () => {
    if (!window.confirm('Envoyer un email de confirmation à TOUS les invités actuels (y compris ceux qui ont déjà RSVP avant) ?')) {
      return;
    }

    try {
      toast.loading('Envoi des emails de confirmation en cours...');
      const response = await axios.post(`${API}/send-confirmations-all`);
      toast.dismiss();
      toast.success(`${response.data.sent} email(s) de confirmation envoyé(s) avec succès!`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} email(s) n'ont pas pu être envoyés`);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error sending confirmations:', error);
      toast.error('Erreur lors de l\'envoi des confirmations. Veuillez réessayer.');
    }
  };

  const getParticipantTypeLabel = (type) => {
    const labels = {
      guest: 'Invité',
      media: 'Média',
      volunteer: 'Bénévole'
    };
    return labels[type] || type;
  };

  const getParticipantTypeBadge = (type) => {
    const colors = {
      guest: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      media: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      volunteer: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Event Thumbnail */}
        <div className="mb-8 rounded-lg overflow-hidden border border-gold/20">
          <div className="relative h-48 md:h-64">
            <img 
              src="https://customer-assets.emergentagent.com/job_tapis-rouge-rsvp/artifacts/e25pelb3_M2MS1_HIGHRES_20.9.png"
              alt="Le Meilleur des Deux Mondes - Avant-Première"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="inline-block px-3 py-1 bg-red-600 text-white text-xs font-semibold mb-2 rounded">
                AVANT-PREMIÈRE VIP
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">Le Meilleur des Deux Mondes</h2>
              <p className="text-gray-300 text-sm mt-1">1er décembre 2025 • 18h30</p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gold mb-2">Liste des Invités</h1>
            <p className="text-gray-400">Gestion des RSVPs</p>
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <Button 
              onClick={sendConfirmationsToAll}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Envoyer Confirmations
            </Button>
            <Button 
              onClick={sendReminder7Days}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center gap-2"
            >
              <Bell className="w-4 h-4" />
              Rappel 7 jours
            </Button>
            <Button 
              onClick={sendReminder24Hours}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold flex items-center gap-2"
            >
              <Clock className="w-4 h-4" />
              Rappel 24h
            </Button>
            <Button 
              onClick={exportToExcel}
              className="bg-gold hover:bg-gold/90 text-black font-semibold flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exporter en Excel
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-gray-900 border-gold/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Total RSVPs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gold">{stats.total}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-900 border-blue-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Invités</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">{stats.regularGuests}</div>
              <div className="text-xs text-gray-500 mt-1">Max: 70</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-purple-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Médias</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-400">{stats.media}</div>
              <div className="text-xs text-gray-500 mt-1">Pas de limite</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-green-500/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Bénévoles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">{stats.volunteers}</div>
              <div className="text-xs text-gray-500 mt-1">Max: 5</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gold/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Total Personnes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gold">{stats.totalAttendees}</div>
            </CardContent>
          </Card>
        </div>

        {/* Guest List */}
        <div className="space-y-4">
          {guests.map((guest) => (
            <Card key={guest.id} className="bg-gray-900 border-gray-800 hover:border-gold/30 transition-colors">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <h3 className="text-xl font-semibold text-white">{guest.name}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getParticipantTypeBadge(guest.participantType)}`}>
                        {getParticipantTypeLabel(guest.participantType)}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium border bg-gold/20 text-gold border-gold/30">
                        {1 + guest.additionalGuests} personne{(1 + guest.additionalGuests) > 1 ? 's' : ''}
                      </span>
                      {guest.waitlist && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-orange-500/20 text-orange-400 border-orange-500/30">
                          Liste d'attente
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Mail className="w-4 h-4" />
                        <span>{guest.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Phone className="w-4 h-4" />
                        <span>{guest.phone}</span>
                      </div>
                      {guest.business && (
                        <div className="flex items-center gap-2 text-gray-400">
                          <Building2 className="w-4 h-4" />
                          <span>{guest.business}</span>
                        </div>
                      )}
                      {guest.mediaName && (
                        <div className="flex items-center gap-2 text-purple-400">
                          <Users className="w-4 h-4" />
                          <span>Média: {guest.mediaName}</span>
                        </div>
                      )}
                    </div>

                    {guest.additionalGuests > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-800">
                        <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                          <UserCheck className="w-4 h-4" />
                          <span className="font-medium">
                            {guest.additionalGuests} invité{guest.additionalGuests > 1 ? 's' : ''} supplémentaire{guest.additionalGuests > 1 ? 's' : ''}:
                          </span>
                        </div>
                        <div className="ml-6 space-y-1">
                          {guest.guestNames.map((guestName, idx) => (
                            <div key={idx} className="text-sm text-gold">
                              • {guestName}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-3">
                    <div className="text-right text-sm text-gray-500">
                      {new Date(guest.createdAt).toLocaleDateString('fr-CA', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <Button
                      onClick={() => handleDelete(guest.id, guest.name)}
                      variant="destructive"
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {guests.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Aucun invité pour le moment</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestList;
